/**
 * InterfaceStore
 * our store about interface (window size, browser supports etc)
 */
import { map } from 'lodash';
import { defineStore } from 'pinia';
import { pinia } from 'plugins/store/index';
import { computed, ref } from 'vue';

interface Thematique extends Obj {
  items: PageThematique[],
  current?: PageThematique,
  currentObject?: string,
  isIntro: boolean;
  isListing: boolean;
  nextVariant?: string;
  fromListing?: boolean;
  showThemeHeader: boolean;
  illustrationSeen: {[name:string] : boolean}
}

const useStore = defineStore('thematique', () => {
  const state = ref<Thematique>({
    items: [],
    currentObject: undefined,
    current: undefined,
    isIntro: false,
    isListing: false,
    nextVariant: undefined,
    fromListing: false,
    showThemeHeader: false,
    illustrationSeen: {
      yns:false,
      maria:false,
      art:false,
      charles:false,
      festival:false,
      metier:false,
      university:false,
      raretes:false
    },
    objects: {}, // Centralized objects store keyed by themeId
  });

  const init = (items: PageThematique[]): void => {
    state.value.items = items.slice();
  }

  const update = (theme: PageThematique) => {
    const isAlreadyUpdated = findTheme(theme.id);
    if (isAlreadyUpdated && isAlreadyUpdated.bgColor) return;

    state.value.items = map(state.value.items, (item) => {
      if (item.id === theme.id) {
        return { ...item, ...theme };
      }
      return item;
    });

    // Update the `objects` structure
    if (theme.listing?.section) {
      state.value.objects[theme.id] = theme.listing.section.flatMap((section) => section.objects || []);
    }

  }
  const getObjects = (themeId: string): any[] => {
    return state.value.objects[themeId] || [];
  };

  const setCurrentObject = (id?:string) => {
    state.value.currentObject = id;
  }

  const findObjectBySlug = (slug: string): any | undefined => {
    // Search across all objects in the centralized `objects`
    for (const themeId in state.value.objects) {
      const object = state.value.objects[themeId].find((obj: any) => obj.slug === slug);
      if (object) {
        return object;
      }
    }
    return undefined;
  };

  const findTheme = (value: string) => {
    return state.value.items.find((item: any) => (item as any)?.url === value || item.id === value);
  }

  const setCurrent = (id?: string): void => {
    state.value.current = !id ? undefined : findTheme(id);
  }


  const toggleIntro = (bool: boolean) => {
    state.value.isIntro = bool;
  }

  const toggleIsListing = (bool: boolean) => {
    state.value.isListing = bool;
  }

  const setNextVariant = (id?: string) => {
    state.value.nextVariant = id;
  }

  const toggleFromListing = (bool: boolean) => {
    state.value.fromListing = bool;
  }

  const setIllustrationSeen = (id:string) => {
    state.value.illustrationSeen[id] = true;
  }

  const toggleThemeHeader = (value:boolean) => {
    state.value.showThemeHeader = value
  }

  return {
    state,

    //getters
    items: computed(() => state.value.items),
    current: computed(() => state.value.current),
    currentObject: computed( () => state.value.currentObject),
    isIntro: computed(() => state.value.isIntro),
    isListing: computed(() => state.value.isListing),
    currentColor: computed(() => state.value.current ? state.value.current.bgColor : '#ffeaf9'),
    nextVariant: computed(() => state.value.nextVariant),
    fromListing: computed(() => state.value.fromListing),
    showThemeHeader: computed( () => state.value.showThemeHeader),
    getBgColorFromURL: (url: string) => {
      switch(url) {
        case "/collection/":
          return "255, 234, 249";

        case "/biographie/":
          return "25, 32, 150";

        case "/credits/":
          return "229, 240, 248";

        default:
        case "/":
          return "255, 234, 249";
      }
    },
    hasSeenIllu: (id:string) => state.value.illustrationSeen[id],

    //actions
    init,
    update,
    findTheme,
    setCurrent,
    findObjectBySlug,
    setCurrentObject,
    getObjects,
    toggleIntro,
    toggleIsListing,
    toggleThemeHeader,
    setNextVariant,
    toggleFromListing,
    setIllustrationSeen,
  }
});

export const store = useStore(pinia);
export default store;

// @ts-expect-error
window.interface = store
